import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {Upload, Edit2, Copy, Check, Search, Filter, Trash2, ArrowLeft} from 'lucide-react'
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

interface Photo {
  _id: string
  fileName: string
  fileUrl: string
  alt: string
  category?: string
  tags?: string[]
  width?: number
  height?: number
  fileSize?: number
  uploadedBy: string
  displayLocations?: string[]  // 新增：顯示位置（如 'homeCarousel'）
  createdAt: string
  updatedAt: string
}

const PhotoManager: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchPhotos()
  }, [])

  const fetchPhotos = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.photos.list({
        sort: { createdAt: -1 },
        limit: 100
      })
      setPhotos(list as Photo[])
    } catch (error) {
      console.error("Failed to fetch photos:", error)
      toast.error("載入照片失敗")
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      const uploadResults = await lumi.tools.file.upload(Array.from(files))

      const photoRecords = uploadResults
        .filter(result => result.fileUrl)
        .map(result => ({
          fileName: result.fileName,
          fileUrl: result.fileUrl!,
          alt: result.fileName.replace(/\.[^/.]+$/, ""),
          category: "general",
          tags: [],
          uploadedBy: lumi.auth.user?.userId || "anonymous",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

      if (photoRecords.length > 0) {
        await lumi.entities.photos.createMany(photoRecords)
        toast.success(`成功上傳 ${photoRecords.length} 張照片`)
        fetchPhotos()
      }

      const failedUploads = uploadResults.filter(result => result.uploadError)
      if (failedUploads.length > 0) {
        failedUploads.forEach(result => {
          toast.error(`${result.fileName} 上傳失敗: ${result.uploadError}`)
        })
      }
    } catch (error) {
      console.error("Upload failed:", error)
      toast.error("上傳失敗")
    } finally {
      setUploading(false)
      event.target.value = ""
    }
  }

  const handleDeletePhotos = async (photoIds: string[]) => {
    if (!confirm(`確定要刪除 ${photoIds.length} 張照片嗎？`)) return

    try {
      const photosToDelete = photos.filter(p => photoIds.includes(p._id))
      const fileUrls = photosToDelete.map(p => p.fileUrl)

      await lumi.tools.file.delete(fileUrls)
      await lumi.entities.photos.deleteMany(photoIds)

      toast.success(`成功刪除 ${photoIds.length} 張照片`)
      setSelectedPhotos([])
      fetchPhotos()
    } catch (error) {
      console.error("Delete failed:", error)
      toast.error("刪除失敗")
    }
  }

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedUrl(url)
    toast.success("URL 已複製")
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  const toggleSelectPhoto = (photoId: string) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    )
  }

  const handleToggleDisplayLocation = async (photoId: string, location: string, checked: boolean) => {
    try {
      const photo = photos.find(p => p._id === photoId)
      if (!photo) return

      let newLocations = photo.displayLocations || []
      if (checked) {
        if (!newLocations.includes(location)) {
          newLocations = [...newLocations, location]
        }
      } else {
        newLocations = newLocations.filter(loc => loc !== location)
      }

      await lumi.entities.photos.update(photoId, {
        displayLocations: newLocations
      })

      // 更新本地狀態
      setPhotos(prev => prev.map(p => 
        p._id === photoId ? { ...p, displayLocations: newLocations } : p
      ))

      toast.success(`已${checked ? '加入' : '移除'}顯示位置`)
    } catch (error) {
      console.error('Failed to update display location:', error)
      toast.error('更新失敗')
    }
  }

  const handleBatchUpdateDisplayLocation = async (location: string, action: 'add' | 'remove') => {
    if (selectedPhotos.length === 0) return
    
    try {
      const updates = selectedPhotos.map(async (photoId) => {
        const photo = photos.find(p => p._id === photoId)
        if (!photo) return

        let newLocations = photo.displayLocations || []
        if (action === 'add') {
          if (!newLocations.includes(location)) {
            newLocations = [...newLocations, location]
          }
        } else {
          newLocations = newLocations.filter(loc => loc !== location)
        }

        await lumi.entities.photos.update(photoId, {
          displayLocations: newLocations
        })

        return { photoId, newLocations }
      })

      const results = await Promise.all(updates)
      
      // 批次更新本地狀態
      setPhotos(prev => prev.map(p => {
        const result = results.find(r => r?.photoId === p._id)
        return result ? { ...p, displayLocations: result.newLocations } : p
      }))

      toast.success(`已批次${action === 'add' ? '加入' : '移除'}顯示位置 (${selectedPhotos.length} 張)`)
      setSelectedPhotos([])
    } catch (error) {
      console.error('Batch update failed:', error)
      toast.error('批次更新失敗')
    }
  }

  const filteredPhotos = photos.filter(photo => {
    const matchesSearch = photo.alt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         photo.fileName.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = categoryFilter === "all" || photo.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  return (
    <div className="p-6 text-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-light tracking-wider mb-2">照片管理</h1>
          <p className="text-gray-400">批量上傳、編輯 Alt、管理照片資源</p>
        </div>

        {/* 上傳區域 */}
        <div className="mb-8">
          <label className="block">
            <div className="border-2 border-dashed border-gray-700 hover:border-gray-500 transition-colors p-12 text-center cursor-pointer bg-gray-950">
              <Upload size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-lg text-gray-300 mb-2">
                {uploading ? "上傳中..." : "點擊或拖曳上傳照片"}
              </p>
              <p className="text-sm text-gray-500">支援 JPG、PNG、WebP 格式</p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
            </div>
          </label>
        </div>

        {/* 工具列 */}
        <div className="mb-6 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4 flex-1">
            {/* 搜尋 */}
            <div className="relative flex-1 max-w-md">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="搜尋檔名或 Alt..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-950 border border-gray-800 text-gray-100 focus:border-gray-600 outline-none"
              />
            </div>

            {/* 分類篩選 */}
            <div className="relative">
              <Filter size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 pr-8 py-2 bg-gray-950 border border-gray-800 text-gray-100 focus:border-gray-600 outline-none appearance-none"
              >
                <option value="all">全部分類</option>
                <option value="product">商品</option>
                <option value="store">門市</option>
                <option value="article">文章</option>
                <option value="general">一般</option>
              </select>
            </div>
          </div>

          {/* 批量操作 */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 border border-gray-700">
              <span className="text-sm text-gray-400">已選取：{selectedPhotos.length} 張</span>
            </div>

            {/* 批次顯示位置 */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 border border-gray-700">
              <span className="text-sm text-gray-400">首頁輪播圖:</span>
              <button
                onClick={() => handleBatchUpdateDisplayLocation('homeCarousel', 'add')}
                disabled={selectedPhotos.length === 0}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm transition-colors"
                title={selectedPhotos.length === 0 ? "請先勾選照片" : "批次加入首頁輪播圖"}
              >
                加入
              </button>
              <button
                onClick={() => handleBatchUpdateDisplayLocation('homeCarousel', 'remove')}
                disabled={selectedPhotos.length === 0}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white text-sm transition-colors"
                title={selectedPhotos.length === 0 ? "請先勾選照片" : "批次移除首頁輪播圖"}
              >
                移除
              </button>
            </div>

            {/* 批次刪除 */}
            <button
              onClick={() => handleDeletePhotos(selectedPhotos)}
              disabled={selectedPhotos.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white transition-colors"
              title={selectedPhotos.length === 0 ? "請先勾選照片" : "批次刪除"}
            >
              <Trash2 size={18} />
              批次刪除
            </button>
          </div>
        </div>

        {/* 照片網格 */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">載入中...</div>
        ) : filteredPhotos.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {searchQuery || categoryFilter !== "all" ? "沒有符合的照片" : "尚無照片，請上傳"}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredPhotos.map((photo) => (
              <div
                key={photo._id}
                className="group relative bg-gray-950 border border-gray-800 hover:border-gray-600 transition-all overflow-hidden"
              >
                {/* 選取框 */}
                <input
                  type="checkbox"
                  checked={selectedPhotos.includes(photo._id)}
                  onChange={() => toggleSelectPhoto(photo._id)}
                  className="absolute top-2 left-2 z-10 w-5 h-5 cursor-pointer"
                />

                {/* 圖片 */}
                <div className="aspect-square bg-gray-900 flex items-center justify-center overflow-hidden">
                  <img
                    src={photo.fileUrl}
                    alt={photo.alt}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* 資訊 */}
                <div className="p-3">
                  <p className="text-sm text-gray-300 truncate mb-1" title={photo.fileName}>
                    {photo.fileName}
                  </p>
                  <p className="text-xs text-gray-500 truncate mb-2" title={photo.alt}>
                    Alt: {photo.alt || "未設定"}
                  </p>
                  {photo.category && (
                    <span className="inline-block px-2 py-1 text-xs bg-gray-800 text-gray-400 mb-2">
                      {photo.category}
                    </span>
                  )}
                  {/* 顯示位置標籤 */}
                  {photo.displayLocations && photo.displayLocations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {photo.displayLocations.map((loc, idx) => (
                        <span key={idx} className="inline-block px-2 py-1 text-xs bg-blue-800 text-blue-300">
                          {loc === 'homeCarousel' ? '首頁輪播' : loc}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* 操作按鈕與顯示位置設定 */}
                <div 
                  className="absolute inset-0 bg-black/90 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                >
                  <div className="flex flex-col h-full pointer-events-auto">
                    {/* 顯示位置複選框 */}
                    <div className="p-3 space-y-2">
                      <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer hover:text-white">
                        <input
                          type="checkbox"
                          checked={photo.displayLocations?.includes('homeCarousel') || false}
                          onChange={(e) => {
                            e.stopPropagation()
                            handleToggleDisplayLocation(photo._id, 'homeCarousel', e.target.checked)
                          }}
                          className="w-4 h-4"
                        />
                        首頁輪播圖
                      </label>
                    </div>
                    
                    {/* 操作按鈕 */}
                    <div className="flex items-center justify-center gap-2 mt-auto mb-3">
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          navigate(`/admin/photos/edit/${photo._id}`)
                        }}
                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white transition-colors relative z-10"
                        title="編輯"
                        type="button"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCopyUrl(photo.fileUrl)
                        }}
                        className="p-2 bg-green-600 hover:bg-green-700 text-white transition-colors"
                        title="複製 URL"
                      >
                        {copiedUrl === photo.fileUrl ? <Check size={18} /> : <Copy size={18} />}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeletePhotos([photo._id])
                        }}
                        className="p-2 bg-red-600 hover:bg-red-700 text-white transition-colors"
                        title="刪除"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default PhotoManager
